import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import API from "../../../util/api";
import ProfilePage from "../../ProfilePage/ProfilePage";

const StatusWrapper = styled.div`
  padding: 32px;
  text-align: center;
  color: ${({ colors }) => colors.labelText};
`;

const LoadingText = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const ErrorText = styled.div`
  font-size: 16px;
  color: #c53030;
`;

const buildUserRecord = (detail = {}, selected = {}, fallbackUserId) => {
  const resolvedDetail = detail?.user ?? detail;
  const base = Array.isArray(resolvedDetail)
    ? (resolvedDetail.find((entry) => entry && typeof entry === "object") ||
        resolvedDetail[0] ||
        {})
    : resolvedDetail && typeof resolvedDetail === "object"
    ? resolvedDetail
    : {};
  const membership = base.membership && typeof base.membership === "object"
    ? base.membership
    : {};
  const row = selected || {};

  const pick = (...keys) => {
    for (const key of keys) {
      if (membership[key] !== undefined && membership[key] !== null && membership[key] !== "") {
        return membership[key];
      }
      if (base[key] !== undefined && base[key] !== null && base[key] !== "") {
        return base[key];
      }
      if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
        return row[key];
      }
    }
    return "";
  };

  const newsletterValue =
    membership.news_letter ??
    membership.newsletter ??
    base.newsletter_signup ??
    row.newsletter_signup ??
    "";

  return {
    user_id: pick("user_id", "id") || fallbackUserId,
    email: pick("email"),
    first_name: pick("first_name", "first"),
    last_name: pick("last_name", "last"),
    title: pick("title"),
    address: pick("address", "street"),
    address2: pick("address2", "address_2"),
    city: pick("city", "org_city"),
    state: pick("state", "state_code", "province", "provence", "org_state"),
    zip: pick("zip", "postal_code", "zip_code", "org_zip"),
    phone: pick("phone"),
    ext: pick("ext"),
    fax: pick("fax"),
    newsletter_signup: newsletterValue,
    news_letter: membership.news_letter,
    roles: base.roles || row.roles || [],
    is_active:
      membership.is_active ?? base.is_active ?? row.is_active ?? "",
    membership,
    rawDetail: detail,
  };
};

function AdminUserProfilePage({ colors }) {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const preloadedUser = useMemo(() => {
    if (!location.state) return null;
    const { userDetail, selectedUser } = location.state || {};
    if (!userDetail && !selectedUser) return null;
    return buildUserRecord(userDetail, selectedUser, userId);
  }, [location.state, userId]);

  const fetchStatusRef = useRef({ userId: null, inFlight: false });
  const preloadedUserRef = useRef(preloadedUser);

  useEffect(() => {
    preloadedUserRef.current = preloadedUser;
  }, [preloadedUser]);

  const [userData, setUserData] = useState(preloadedUser);
  const [loading, setLoading] = useState(!preloadedUser);
  const [error, setError] = useState("");
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoadingState, setSubscriptionsLoadingState] = useState(false);
  const [subscriptionsErrorMessage, setSubscriptionsErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    if (!userId) {
      setError("Invalid user id.");
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const alreadyFetchingSameUser =
      fetchStatusRef.current.inFlight &&
      fetchStatusRef.current.userId === userId;
    if (alreadyFetchingSameUser) {
      return () => {
        cancelled = true;
      };
    }

    fetchStatusRef.current = { userId, inFlight: true };

    const fetchDetailAndSubscriptions = async () => {
      setLoading(true);
      setSubscriptionsLoadingState(true);
      setSubscriptionsErrorMessage("");
      try {
        const detailResponse = await API.getUserDetail({ user_id: userId, id: userId });

        if (cancelled) return;

        const fallbackSelected =
          preloadedUserRef.current || location.state?.selectedUser || {};
        const normalized = buildUserRecord(
          detailResponse,
          fallbackSelected,
          userId
        );
        setUserData(normalized);
        setError("");

        try {
          const subscriptionsResponse = await API.getTeacherSubscriptions({ user_id: userId });
          if (cancelled) return;
          const rawSubscriptions = subscriptionsResponse?.subscription;
          const normalizedSubscriptions = Array.isArray(rawSubscriptions)
            ? rawSubscriptions
            : rawSubscriptions
            ? Object.values(rawSubscriptions)
            : [];
          setSubscriptions(normalizedSubscriptions);
          if (!normalizedSubscriptions?.length) {
            setSubscriptionsErrorMessage("No teacher subscriptions found.");
          }
        } catch (subsErr) {
          if (cancelled) return;
          setSubscriptions([]);
          setSubscriptionsErrorMessage("No teacher subscriptions found.");
        }
      } catch (err) {
        if (cancelled) return;
        if (!preloadedUser) {
          setError(err?.message || "Unable to load user profile.");
        }
        setSubscriptions([]);
        setSubscriptionsErrorMessage("No teacher subscriptions found.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSubscriptionsLoadingState(false);
        }
        if (fetchStatusRef.current.userId === userId) {
          fetchStatusRef.current.inFlight = false;
        }
      }
    };

    fetchDetailAndSubscriptions();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userData && loading) {
    return (
      <StatusWrapper colors={colors}>
        <LoadingText>Loading user profile…</LoadingText>
      </StatusWrapper>
    );
  }

  if (!userData && error) {
    return (
      <StatusWrapper colors={colors}>
        <ErrorText>{error}</ErrorText>
      </StatusWrapper>
    );
  }

  return (
    <>
      {error && (
        <StatusWrapper colors={colors}>
          <ErrorText>{error}</ErrorText>
        </StatusWrapper>
      )}
      {userData && (
        <ProfilePage
          colors={colors}
          userOverride={userData}
          teacherSubscriptionsOverride={subscriptions}
          teacherSubscriptionsLoadingOverride={subscriptionsLoadingState}
          teacherSubscriptionsErrorOverride={subscriptionsErrorMessage}
          onCancel={() => navigate(-1)}
        />
      )}
    </>
  );
}

export default AdminUserProfilePage;
